import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import { StatusBadge, ScopeBadge } from "./Badges";

export default function LicensingImageryTable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchWithAuth("/api/v1/platform/licensing/imagery");
        if (!res.ok) throw new Error("Failed to load imagery licensing");
        setData(await res.json());
      } catch (e) {
        console.error(e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center pt-6">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }
  if (error) return <div className="text-xs text-red-400">{error}</div>;
  if (!data) return null;

  const { rows = [], totals = {} } = data;

  if (rows.length === 0) {
    return <div className="text-sm text-muted">No imagery entitlements found.</div>;
  }

  return (
    <div className="overflow-auto h-full">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-bg border-b border-bg-border">
          <tr className="text-left text-xs font-mono uppercase tracking-wide text-muted">
            <th className="px-3 py-2">Org</th>
            <th className="px-3 py-2">SKU</th>
            <th className="px-3 py-2">Dataset</th>
            <th className="px-3 py-2">Scope</th>
            <th className="px-3 py-2">AOI</th>
            <th className="px-3 py-2 text-right">Purchased</th>
            <th className="px-3 py-2 text-right">Consumed</th>
            <th className="px-3 py-2 text-right">% Used</th>
            <th className="px-3 py-2">Term</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>

        <tbody>
          {rows.map(r => {
            const termStart = new Date(r.term_start).toLocaleDateString();
            const termEnd = r.term_end
              ? new Date(r.term_end).toLocaleDateString()
              : "—";
            const expiringSoon = r.term_end
              ? (new Date(r.term_end) - new Date()) / (1000 * 60 * 60 * 24) < 30
              : false;
            const highUsage = Number(r.pct_used) >= 80;

            return (
              <tr
                key={r.entitlement_id}
                className="border-b border-bg-border hover:bg-bg-soft"
              >
                <td className="px-3 py-2 font-medium">{r.org_name}</td>
                <td className="px-3 py-2">{r.sku_display_name}</td>
                <td className="px-3 py-2 text-gray-300">
                  {r.dataset_basin}.{r.dataset_region}{"."}
                  <span className="text-gray-300">{r.dataset_acquisition_date}</span>
                </td>
                <td className="px-3 py-2"><ScopeBadge scope={r.scope} /></td>
                <td className="px-3 py-2 text-gray-400">{r.coverage_aoi_name || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {Number(r.sq_mi_purchased).toFixed(1)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-300">
                  {Number(r.sq_mi_consumed).toFixed(1)}
                </td>
                <td className={`px-3 py-2 text-right tabular-nums ${highUsage ? "text-yellow-400" : ""}`}>
                  {Number(r.pct_used).toFixed(1)}%
                </td>
                <td className="px-3 py-2">{termStart} – {termEnd}</td>
                <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
              </tr>
            );
          })}
        </tbody>

        <tfoot className="border-t border-bg-border bg-bg-soft sticky bottom-0">
          <tr>
            <td colSpan={5} className="px-3 py-2 text-right font-medium">
              Totals ({totals.entitlement_count} entitlements)
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {Number(totals.total_purchased).toFixed(1)}
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {Number(totals.total_consumed).toFixed(1)}
            </td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}